import { Component, Input, forwardRef, ViewChild, ElementRef, HostListener, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { FormErrorComponent } from '../form-error/form-error.component';

export interface SelectOption {
  value: any;
  label: string;
  disabled?: boolean;
}

/**
 * IsSelectFieldComponent
 *
 * Select customizado com dropdown moderno, busca integrada e clear.
 */
@Component({
  selector: 'is-select-field',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, FormErrorComponent],
  templateUrl: './select-field.component.html',
  styleUrls: ['./select-field.component.scss'],
  host: { '(document:mousedown)': 'onDocumentClick($event)' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectFieldComponent),
      multi: true,
    },
  ],
})
export class SelectFieldComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label = '';
  @Input() placeholder = 'Selecione...';
  @Input() options: SelectOption[] = [];
  @Input() required = false;
  @Input() helpText = '';
  @Input() control: AbstractControl | null = null;

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('selectWrapper') selectWrapper?: ElementRef<HTMLDivElement>;

  value: any = '';
  isDisabled = false;
  isOpen = false;
  searchTerm = '';
  filteredOptions: SelectOption[] = [];
  highlightedIndex = -1;

  private onChange: (val: any) => void = () => {};
  private onTouched: () => void = () => {};
  private controlSub: Subscription | null = null;

  ngOnInit(): void {
    this.syncFromControl();
  }

  ngOnDestroy(): void {
    this.controlSub?.unsubscribe();
  }

  /**
   * Sincroniza o valor interno com o AbstractControl passado via @Input().
   * Assina valueChanges para reagir a patchValue/setValue/reset externos.
   */
  private syncFromControl(): void {
    if (!this.control) return;

    // Sincroniza valor inicial do FormControl
    this.value = this.control.value ?? '';

    // Observa mudanças externas no FormControl
    this.controlSub = this.control.valueChanges.subscribe((val) => {
      this.value = val ?? '';
    });
  }

  get hasError(): boolean {
    return !!(this.control?.invalid && (this.control?.dirty || this.control?.touched));
  }

  get selectedLabel(): string {
    const opt = this.options.find((o) => o.value == this.value);
    return opt?.label || '';
  }

  // ─── ControlValueAccessor ───────────────────────────────────────────────────

  writeValue(val: any): void {
    this.value = val ?? '';
  }

  registerOnChange(fn: (val: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  // ─── Dropdown Control ───────────────────────────────────────────────────────

  toggle(): void {
    if (this.isDisabled) return;
    this.isOpen ? this.close() : this.open();
  }

  open(): void {
    this.isOpen = true;
    this.searchTerm = '';
    this.filteredOptions = [...this.options];
    this.highlightedIndex = this.options.findIndex((o) => o.value == this.value);
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 50);
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.searchTerm = '';
    this.highlightedIndex = -1;
    this.onTouched();
  }

  selectOption(opt: SelectOption): void {
    if (opt.disabled) return;
    this.value = opt.value;
    this.onChange(opt.value);
    this.close();
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.value = '';
    this.onChange('');
  }

  filterOptions(): void {
    const term = this.searchTerm.toLowerCase();
    this.filteredOptions = this.options.filter((o) =>
      o.label.toLowerCase().includes(term)
    );
    this.highlightedIndex = 0;
  }

  // ─── Click Outside ──────────────────────────────────────────────────────────

  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    const target = event.target as HTMLElement;
    if (!this.selectWrapper?.nativeElement?.contains(target)) {
      this.close();
    }
  }

  // ─── Keyboard Navigation ───────────────────────────────────────────────────

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggle();
        break;
      case 'Escape':
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen) { this.open(); return; }
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredOptions.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
        break;
    }
  }

  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.filteredOptions[this.highlightedIndex]) {
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredOptions.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
        break;
    }
  }
}
